//*Libraries imports
import { CreditCard, Plus, Check } from "lucide-react";

//*Components Imports
import { CardBrandBadge } from "@/components/ui/CardBrandBadge";

//*Types imports
import type { ClientCreditCard } from "@/schemas/client";

interface SavedCardsListProps {
  cards: ClientCreditCard[];
  selectedToken: string | null;
  onSelect: (token: string) => void;
  onAddNew: () => void;
  loading?: boolean;
}

export function SavedCardsList({
  cards,
  selectedToken,
  onSelect,
  onAddNew,
  loading = false,
}: SavedCardsListProps) {
  return (
    <div className="bg-[#FCFCFC] rounded-[18px] p-5 border border-[#EDF2F6] flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <CreditCard className="size-5 text-claroprimaria-01" />
        <h3 className="font-bold text-clarotexto-201">Meus Cartões</h3>
      </div>

      <div className="flex flex-col gap-3">
        {cards.map((card, index) => {
          const isSelected = selectedToken === card.creditCardToken;

          return (
            <button
              key={card.creditCardToken || index}
              type="button"
              onClick={() =>
                card.creditCardToken && onSelect(card.creditCardToken)
              }
              disabled={loading}
              className={`flex items-center justify-between p-5 border rounded-xl transition-all text-left group relative ${
                isSelected
                  ? "border-claroprimaria-01 bg-blue-50/50"
                  : "bg-white border-[#EDF2F6] hover:border-claroprimaria-01/50 hover:bg-gray-50"
              }`}
            >
              <div className="flex items-center gap-4">
                <div
                  className={`flex items-center justify-center size-12 rounded-full transition-colors ${
                    isSelected
                      ? "bg-white border border-blue-100"
                      : "bg-gray-100/50 border border-transparent group-hover:bg-white group-hover:border-blue-100"
                  }`}
                >
                  <CreditCard
                    className={`size-6 ${
                      isSelected
                        ? "text-claroprimaria-01"
                        : "text-gray-400 group-hover:text-claroprimaria-01"
                    }`}
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <span
                      className={`font-semibold text-base ${isSelected ? "text-claroprimaria-01" : "text-gray-700"}`}
                    >
                      •••• {card.creditCardNumber?.slice(-4) || "????"}
                    </span>
                    {card.creditCardBrand && (
                      <CardBrandBadge brand={card.creditCardBrand} />
                    )}
                  </div>
                  <span className="text-xs text-gray-500 font-medium uppercase tracking-wide">
                    Crédito
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div
                  className={`flex items-center justify-center size-6 rounded-full border transition-all ${
                    isSelected
                      ? "border-claroprimaria-01 bg-claroprimaria-01"
                      : "border-gray-300 bg-white group-hover:border-claroprimaria-01"
                  }`}
                >
                  {isSelected && <Check className="size-3.5 text-white" />}
                </div>
              </div>
            </button>
          );
        })}

        <button
          type="button"
          onClick={onAddNew}
          disabled={loading}
          className="flex items-center justify-center gap-2 p-3.5 border border-dashed border-gray-300 rounded-xl hover:border-claroprimaria-01 hover:text-claroprimaria-01 hover:bg-blue-50/30 transition-all text-gray-500 mt-1 group"
        >
          <div className="size-6 rounded-full bg-gray-100 flex items-center justify-center group-hover:bg-blue-100 transition-colors">
            <Plus className="size-3.5" />
          </div>
          <span className="font-medium text-sm">Adicionar novo cartão</span>
        </button>
      </div>
    </div>
  );
}
